import React, { useEffect, useMemo, useState } from "react";
import { checkBreach } from "@/lib/breachCheck";

/**
 * PasswordStrengthMeter — colored bar + label under a password field.
 * Scoring: length + character classes − obvious weaknesses. Not a
 * replacement for zxcvbn, but zero-dep and instant.
 *
 * Also fires a debounced HaveIBeenPwned k-anonymous lookup (fully
 * client-side hash — password never leaves the browser) and shows
 * "Seen in N breaches — pick another" when the password has leaked.
 */

const COMMON = new Set([
  "password", "password1", "12345678", "123456789", "qwerty", "qwerty123",
  "letmein", "welcome", "admin", "iloveyou", "monkey", "dragon",
  "abc123", "111111", "123123", "password!", "changeme", "test1234",
]);

export function scorePassword(pw) {
  if (!pw) return { score: 0, label: "", color: "#334155", tips: [] };
  const p = pw.toLowerCase();
  if (COMMON.has(p)) {
    return { score: 1, label: "Too common", color: "#EF4444", tips: ["Pick something unique — this password is on public breach lists."] };
  }
  let s = 0;
  const tips = [];
  if (pw.length >= 12) s += 2; else if (pw.length >= 8) s += 1; else tips.push("Use at least 12 characters");
  if (/[a-z]/.test(pw)) s += 1; else tips.push("Add a lowercase letter");
  if (/[A-Z]/.test(pw)) s += 1; else tips.push("Add an uppercase letter");
  if (/\d/.test(pw)) s += 1; else tips.push("Add a digit");
  if (/[^A-Za-z0-9]/.test(pw)) s += 1; else tips.push("Add a symbol (!@#…)");
  if (/(.)\1{2,}/.test(pw)) { s -= 1; tips.push("Avoid 3+ repeated characters"); }
  if (/(0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|qwer|asdf|zxcv)/i.test(pw)) {
    s -= 1;
    tips.push("Avoid sequential runs (1234, abcd, qwerty…)");
  }
  s = Math.max(0, Math.min(5, s));

  const table = [
    { label: "Very weak", color: "#EF4444" },
    { label: "Weak",      color: "#EF4444" },
    { label: "Fair",      color: "#F59E0B" },
    { label: "Good",      color: "#EAB308" },
    { label: "Strong",    color: "#10B981" },
    { label: "Excellent", color: "#00F0FF" },
  ];
  return { score: s, ...table[s], tips: tips.slice(0, 2) };
}

export default function PasswordStrengthMeter({ password, testId = "pw-strength", onBreachChange }) {
  const { score, label, color, tips } = useMemo(() => scorePassword(password), [password]);
  const [breach, setBreach] = useState({ count: 0, checking: false });

  useEffect(() => {
    // Skip API call for empty or super-short passwords (not worth the round-trip)
    if (!password || password.length < 4) {
      setBreach({ count: 0, checking: false });
      onBreachChange && onBreachChange(false);
      return;
    }
    let cancelled = false;
    setBreach(b => ({ ...b, checking: true }));
    const t = setTimeout(async () => {
      const result = await checkBreach(password);
      if (!cancelled) {
        setBreach({ ...result, checking: false });
        onBreachChange && onBreachChange((result.count || 0) > 0);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  if (!password) return null;
  const segments = 5;
  const filled = Math.max(1, Math.round((score / 5) * segments));
  const breachBad = breach.count && breach.count > 0;

  return (
    <div data-testid={testId} className="space-y-1">
      <div className="flex gap-1">
        {Array.from({ length: segments }).map((_, i) => (
          <div key={i} className="h-1 flex-1 rounded"
               style={{ background: i < filled ? (breachBad ? "#EF4444" : color) : "#1E2638" }}/>
        ))}
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span data-testid={`${testId}-label`} className="text-[10px] font-mono uppercase tracking-widest" style={{ color: breachBad ? "#EF4444" : color }}>{breachBad ? "Breached" : label}</span>
        {breachBad ? (
          <span data-testid={`${testId}-breach`} className="text-[10px] font-mono text-[#EF4444] truncate text-right">
            Seen in {breach.count.toLocaleString()} breach{breach.count === 1 ? "" : "es"} — pick another
          </span>
        ) : breach.checking ? (
          <span className="text-[10px] font-mono text-[#94A3B8] truncate text-right">Checking breach lists…</span>
        ) : tips.length > 0 ? (
          <span className="text-[10px] font-mono text-[#94A3B8] truncate text-right">{tips[0]}</span>
        ) : null}
      </div>
    </div>
  );
}
